import moment from 'moment'
import {filters} from '@/filters'
import {HistoryService} from '@/services/counteragent'
import {
  CELL_BORDER, COLORS, DATE_FORMAT, MONEY_FORMAT, USD_FORMAT,
  createWorkbook, downloadWorkbook, styleHeaderRow, toExcelDate,
} from '@/excel'

const CURRENCY_FORMATS = {uzs: '#,##0" сум"', usd: '#,##0.00" $"'}

/**
 * Builds and downloads an .xlsx of a counteragent card with its full operation history.
 * @param name     counteragent name (for the file name)
 * @param title    card title ("Контрагент #1 • Агент: ...")
 * @param info     [[label, value], ...] header fields as shown on the card
 * @param comment  counteragent comment
 * @param period   {from, to} server-format dates of the history filter
 * @param meta     history type_total from the API
 * @param rows     all history rows of the period
 */
export async function exportCounteragent({name, title, info, comment, period, meta, rows}) {
  const workbook = await createWorkbook()
  const sheet = workbook.addWorksheet('Контрагент', {views: [{showGridLines: false}]})
  sheet.columns = [
    {width: 6},
    {width: 30},
    {width: 18},
    {width: 16},
    {width: 16},
    {width: 18},
    {width: 18},
    {width: 18},
  ]

  sheet.addRow([title]).font = {bold: true, size: 14}
  sheet.addRow([])

  info.forEach(([label, value]) => {
    const row = sheet.addRow([null, label, value || ''])
    row.getCell(2).font = {bold: true}
    sheet.mergeCells(row.number, 3, row.number, 8)
    row.getCell(3).alignment = {wrapText: true, vertical: 'top'}
  })
  if (comment) {
    const row = sheet.addRow([null, 'Комментарий', comment])
    row.getCell(2).font = {bold: true}
    sheet.mergeCells(row.number, 3, row.number, 8)
    row.getCell(3).alignment = {wrapText: true, vertical: 'top'}
  }

  sheet.addRow([])
  sheet.addRow([`История операций: ${filters.date(period.from)} — ${filters.date(period.to)}`]).font = {bold: true, size: 12}

  // Summary block, same figures as the grey block above the history table
  const summaryHeader = sheet.addRow([null, null, 'Дебет', 'Кредит', 'Итог'])
  ;[3, 4, 5].forEach(i => {
    summaryHeader.getCell(i).font = {bold: true}
    summaryHeader.getCell(i).alignment = {horizontal: 'right'}
  })
  const summary = [
    ['В сумах', meta.debet_uzs_summa, meta.credit_uzs_summa, MONEY_FORMAT],
    ['В долларах', meta.debet_usd_summa, meta.credit_usd_summa, USD_FORMAT],
    ['Всего (в сумах)', meta.debet_uzs_total_summa, meta.credit_uzs_total_summa, MONEY_FORMAT],
  ]
  summary.forEach(([label, debet, credit, format]) => {
    const row = sheet.addRow([null, label, debet || 0, credit || 0, (debet || 0) - (credit || 0)])
    row.getCell(2).font = {bold: true}
    row.getCell(3).font = {color: {argb: COLORS.green}}
    row.getCell(4).font = {color: {argb: COLORS.red}}
    ;[3, 4, 5].forEach(i => row.getCell(i).numFmt = format)
  })
  const difference = meta.difference || 0
  const differenceRow = sheet.addRow([null, difference < 0 ? 'Мы должны:' : 'Нам должны:', difference])
  differenceRow.font = {bold: true, color: {argb: difference > 0 ? COLORS.green : COLORS.red}}
  differenceRow.getCell(3).numFmt = MONEY_FORMAT

  sheet.addRow([])
  styleHeaderRow(sheet.addRow(['#', 'Операция', 'Сумма', 'Рег номер', 'Дата', 'Дебет', 'Кредит', 'Сальдо']))

  rows.forEach((item, index) => {
    const row = sheet.addRow([
      index + 1,
      HistoryService.getTitle(item),
      item.total,
      item.document_reg_number || '',
      item.reg_date ? toExcelDate(item.reg_date) : '',
      item.type === 'debet' ? item.total_uzs : null,
      item.type === 'credit' ? item.total_uzs : null,
      item.saldo,
    ])
    row.getCell(1).alignment = {horizontal: 'center'}
    row.getCell(3).numFmt = CURRENCY_FORMATS[item.currency] || MONEY_FORMAT
    row.getCell(5).numFmt = DATE_FORMAT
    row.getCell(5).alignment = {horizontal: 'center'}
    row.getCell(6).numFmt = MONEY_FORMAT
    row.getCell(7).numFmt = MONEY_FORMAT
    row.getCell(8).numFmt = MONEY_FORMAT
    if (item.saldo) row.getCell(8).font = {color: {argb: item.saldo > 0 ? COLORS.green : COLORS.red}}
    row.eachCell({includeEmpty: true}, cell => cell.border = CELL_BORDER)
  })

  const totalRow = sheet.addRow([null, 'Итого:', null, null, null, meta.debet_uzs_total_summa || 0, meta.credit_uzs_total_summa || 0])
  totalRow.font = {bold: true}
  totalRow.getCell(6).numFmt = MONEY_FORMAT
  totalRow.getCell(7).numFmt = MONEY_FORMAT

  await downloadWorkbook(workbook, `Контрагент ${name} ${moment(period.to).format('YYYY-MM-DD')}.xlsx`)
}
